import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { adminUserService } from '../../services/adminUserService';
import { Select, Tooltip } from '../../shared/components/UI';

const UserManagementContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const SearchAndFilters = styled.div`
  background: var(--color-white);
  padding: 1.5rem;
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-md);
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  align-items: center;
`;

const SearchInput = styled.input`
  flex: 1;
  min-width: 300px;
  padding: 0.75rem 1rem;
  border: 2px solid var(--color-gray-300);
  border-radius: var(--border-radius-md);
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: var(--color-primary);
  }
`;

const FilterSelect = styled.select`
  padding: 0.75rem 1rem;
  border: 2px solid var(--color-gray-300);
  border-radius: var(--border-radius-md);
  font-size: 1rem;
  background: var(--color-white);
  
  &:focus {
    outline: none;
    border-color: var(--color-primary);
  }
`;

const UserTable = styled.div`
  background: var(--color-white);
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-md);
  overflow: hidden;
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 1fr 150px 120px 150px 200px 180px;
  background: var(--color-gray-50);
  border-bottom: 1px solid var(--color-gray-200);
  font-weight: 600;
  color: var(--color-gray-700);
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0.5rem;
  }
`;

const TableRow = styled.div<{ $isHighlighted?: boolean }>`
  display: grid;
  grid-template-columns: 1fr 150px 120px 150px 200px 180px;
  border-bottom: 1px solid var(--color-gray-100);
  background: ${props => props.$isHighlighted ? 'var(--color-yellow-50)' : 'var(--color-white)'};
  transition: background-color 0.2s ease;
  
  &:hover {
    background: var(--color-gray-50);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0.5rem;
    padding: 1rem;
  }
`;

const TableCell = styled.div`
  padding: 1rem;
  display: flex;
  align-items: center;
  
  @media (max-width: 768px) {
    padding: 0.25rem 0;
    
    &:before {
      content: attr(data-label);
      font-weight: 600;
      margin-right: 1rem;
      min-width: 100px;
    }
  }
`;

const UserAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--color-primary), var(--color-secondary));
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-white);
  font-weight: 600;
  margin-right: 0.75rem;
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const UserName = styled.div`
  font-weight: 600;
  color: var(--color-gray-900);
`;

const UserEmail = styled.div`
  font-size: 0.875rem;
  color: var(--color-gray-600);
`;

const StatusBadge = styled.span<{ $status: string }>`
  padding: 0.25rem 0.75rem;
  border-radius: var(--border-radius-full);
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  
  ${props => {
    switch (props.$status.toLowerCase()) {
      case 'active':
        return `
          background: #D1FAE5;
          color: #065F46;
        `;
      case 'suspended':
        return `
          background: #FEE2E2;
          color: #991B1B;
        `;
      case 'banned':
        return `
          background: #1F2937;
          color: #F9FAFB;
        `;
      default:
        return `
          background: #F3F4F6;
          color: #374151;
        `;
    }
  }}
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const ActionButton = styled.button<{ $variant: 'primary' | 'warning' | 'danger' }>`
  padding: 0.5rem 1rem;
  border: none;
  border-radius: var(--border-radius-md);
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  ${props => {
    switch (props.$variant) {
      case 'primary':
        return `
          background: var(--color-primary);
          color: var(--color-white);
          &:hover { background: var(--color-primary-600); }
        `;
      case 'warning':
        return `
          background: #F59E0B;
          color: var(--color-white);
          &:hover { background: #D97706; }
        `;
      case 'danger':
        return `
          background: #EF4444;
          color: var(--color-white);
          &:hover { background: #DC2626; }
        `;
    }
  }}
`;

const Pagination = styled.div`
  background: var(--color-white);
  padding: 1rem 1.5rem;
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-md);
  display: flex;
  justify-content: between;
  align-items: center;
  gap: 1rem;
`;

const PaginationInfo = styled.div`
  color: var(--color-gray-600);
  font-size: 0.875rem;
`;

const PaginationButtons = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-left: auto;
`;

const PaginationButton = styled.button<{ $active?: boolean }>`
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--color-gray-300);
  background: ${props => props.$active ? 'var(--color-primary)' : 'var(--color-white)'};
  color: ${props => props.$active ? 'var(--color-white)' : 'var(--color-gray-700)'};
  border-radius: var(--border-radius-md);
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.$active ? 'var(--color-primary-600)' : 'var(--color-gray-100)'};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

interface AdminUser {
  id: string;
  name: string;
  email: string;
  username: string;
  status: 'ACTIVE' | 'SUSPENDED' | 'BANNED';
  joinDate: string;
  lastActive: string | null;
  storiesCount: number;
  role: string;
  suspensionReason?: string;
  suspendedUntil?: string;
}

const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);
  const itemsPerPage = 20;

  // Load users from backend
  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await adminUserService.getUsers({
        page: currentPage - 1,
        size: itemsPerPage,
        search: searchTerm || undefined,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        sortBy: 'createdAt',
        sortDir: 'desc'
      });

      // Transform backend data to frontend format
      const transformedUsers = response.content.map((user: any) => ({
        id: user.id,
        name: user.name,
        email: user.email,
        username: user.username,
        status: user.status,
        joinDate: user.joinDate,
        lastActive: user.lastActive,
        storiesCount: user.storiesCount,
        role: user.role,
        suspensionReason: user.suspensionReason,
        suspendedUntil: user.suspendedUntil
      }));

      setUsers(transformedUsers);
      setTotalPages(response.totalPages);
      setTotalElements(response.totalElements);
    } catch (err) {
      console.error('Failed to load users:', err);
      setError('Failed to load users. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Load users on component mount and when filters change
  useEffect(() => {
    loadUsers();
  }, [currentPage, searchTerm, statusFilter]);

  const handleUserAction = async (userId: string, action: 'warn' | 'suspend' | 'ban' | 'activate') => {
    try {
      switch (action) {
        case 'suspend':
          const reason = prompt('Enter suspension reason:');
          if (!reason) return;
          await adminUserService.suspendUser(userId, reason);
          break;
        case 'ban':
          const banReason = prompt('Enter ban reason:');
          if (!banReason) return;
          await adminUserService.banUser(userId, banReason);
          break;
        case 'activate':
          await adminUserService.activateUser(userId);
          break;
        case 'warn':
          const warning = prompt('Enter warning message:');
          if (!warning) return;
          await adminUserService.warnUser(userId, warning);
          break;
      }
      
      // Reload users after action
      await loadUsers();
    } catch (error) {
      console.error(`Failed to ${action} user:`, error);
      alert(`Failed to ${action} user. Please try again.`);
    }
  };

  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  return (
    <UserManagementContainer>
      {/* Search and Filters */}
      <SearchAndFilters>
        <SearchInput
          type="text"
          placeholder="Search users by name or email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <Select
          label="Status:"
          value={statusFilter}
          onChange={(value) => setStatusFilter(value)}
          options={[
            { value: 'all', label: 'All Status' },
            { value: 'ACTIVE', label: 'Active' },
            { value: 'SUSPENDED', label: 'Suspended' },
            { value: 'BANNED', label: 'Banned' }
          ]}
        />
        <Tooltip content="Export user list to CSV file" position="top">
          <button className="btn btn-primary">
            Export Users
          </button>
        </Tooltip>
      </SearchAndFilters>

      {/* User Table */}
      {loading && <div>Loading users...</div>}
      {error && <div style={{ color: 'red', padding: '1rem' }}>{error}</div>}
      {!loading && !error && (
        <UserTable>
          <TableHeader>
            <TableCell>User</TableCell>
            <TableCell>Stories</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Join Date</TableCell>
            <TableCell>Last Active</TableCell>
            <TableCell>Actions</TableCell>
          </TableHeader>

        {users.map(user => (
          <TableRow key={user.id} $isHighlighted={user.status === 'SUSPENDED'}>
            <TableCell data-label="User">
              <UserAvatar>{getInitials(user.name)}</UserAvatar>
              <UserInfo>
                <UserName>{user.name}</UserName>
                <UserEmail>{user.email}</UserEmail>
              </UserInfo>
            </TableCell>
            <TableCell data-label="Stories">
              {user.storiesCount}
            </TableCell>
            <TableCell data-label="Status">
              <StatusBadge $status={user.status.toLowerCase()}>
                {user.status}
              </StatusBadge>
            </TableCell>
            <TableCell data-label="Join Date">
              {new Date(user.joinDate).toLocaleDateString()}
            </TableCell>
            <TableCell data-label="Last Active">
              {user.lastActive ? new Date(user.lastActive).toLocaleDateString() : 'Never'}
            </TableCell>
            <TableCell data-label="Actions">
              <ActionButtons>
                {user.status === 'ACTIVE' && (
                  <>
                    <Tooltip content="Send a warning message to this user" position="top">
                      <ActionButton
                        $variant="warning"
                        onClick={() => handleUserAction(user.id, 'warn')}
                      >
                        Warn
                      </ActionButton>
                    </Tooltip>
                    <Tooltip content="Temporarily suspend this user's account" position="top">
                      <ActionButton
                        $variant="danger"
                        onClick={() => handleUserAction(user.id, 'suspend')}
                      >
                        Suspend
                      </ActionButton>
                    </Tooltip>
                  </>
                )}
                {user.status === 'SUSPENDED' && (
                  <>
                    <Tooltip content="Restore this user's account access" position="top">
                      <ActionButton
                        $variant="primary"
                        onClick={() => handleUserAction(user.id, 'activate')}
                      >
                        Activate
                      </ActionButton>
                    </Tooltip>
                    <Tooltip content="Permanently ban this user from the platform" position="top">
                      <ActionButton
                        $variant="danger"
                        onClick={() => handleUserAction(user.id, 'ban')}
                      >
                        Ban
                      </ActionButton>
                    </Tooltip>
                  </>
                )}
                {user.status === 'BANNED' && (
                  <Tooltip content="Remove ban and restore user access" position="top">
                    <ActionButton
                      $variant="primary"
                      onClick={() => handleUserAction(user.id, 'activate')}
                    >
                      Unban
                    </ActionButton>
                  </Tooltip>
                )}
              </ActionButtons>
            </TableCell>
          </TableRow>
        ))}
        </UserTable>
      )}

      {/* Pagination */}
      <Pagination>
        <PaginationInfo>
          Showing {(currentPage - 1) * itemsPerPage + 1}-{Math.min(currentPage * itemsPerPage, totalElements)} of {totalElements} users
        </PaginationInfo>
        <PaginationButtons>
          <PaginationButton
            disabled={currentPage === 1}
            onClick={() => setCurrentPage(currentPage - 1)}
          >
            Previous
          </PaginationButton>
          
          {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
            <PaginationButton
              key={page}
              $active={page === currentPage}
              onClick={() => setCurrentPage(page)}
            >
              {page}
            </PaginationButton>
          ))}
          
          <PaginationButton
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage(currentPage + 1)}
          >
            Next
          </PaginationButton>
        </PaginationButtons>
      </Pagination>
    </UserManagementContainer>
  );
};

export default UserManagement;